<script>
  import { createEventDispatcher } from 'svelte';
  import * as yup from 'yup';
  import isEmpty from 'lodash/isEmpty';
  import isEqual from 'lodash/isEqual';
  import dayjs from 'dayjs';
  import utc from 'dayjs/plugin/utc';
  import timezone from 'dayjs/plugin/timezone';

  import { Checkbox, InlineNotification, Modal, NumberInput, Tag } from 'carbon-components-svelte';

  import LoadingSpinner from '../../../../components/LoadingSpinner.svelte';
  import { getServerErrorMessage, formatDurationUnitsToText } from '../../../../utils';

  export let feedMetadata = {},
    entitlementMetadata = {},
    isDefaultDelay,
    activeDelayText,
    defaultDelayText,
    serviceId,
    entitlementId,
    feedName,
    onSaveMetadata,
    onDeleteMetadata,
    open = false;

  let loading = false,
    resultNotification,
    errors = {},
    metadataToUpdate = {
      delayMinutes: entitlementMetadata?.delayMinutes || 0,
      delayHours: entitlementMetadata?.delayHours || 0,
      delayDays: entitlementMetadata?.delayDays || 0,
      delayMonths: entitlementMetadata?.delayMonths || 0,
      calendarMonthDelay: entitlementMetadata?.calendarMonthDelay || false,
    };

  dayjs.extend(utc);
  dayjs.extend(timezone);

  const dispatch = createEventDispatcher();

  const schema = yup.object().shape({
    delayMinutes: yup
      .number()
      .typeError('Minutes must be a number')
      .min(0, 'Minutes must be greater than or equal to 0')
      .max(59, 'Minutes must be less than or equal to 59'),
    delayHours: yup
      .number()
      .typeError('Hours must be a number')
      .min(0, 'Hours must be greater than or equal to 0')
      .max(23, 'Hours must be less than or equal to 23'),
    delayDays: yup.number().typeError('Days must be a number').min(0, 'Days must be greater than or equal to 0'),
    delayMonths: yup.number().typeError('Months must be a number').min(0, 'Months must be greater than or equal to 0'),
    calendarMonthDelay: yup.boolean().default(false),
  });

  async function updateEntitlementMetadata() {
    if (isSameAsDefaultOrSaved) {
      resultNotification = {
        kind: 'info',
        title: 'Update Your Input:',
        subtitle: 'The submitted values are either the defaults or the same as the current ones.',
      };
      return;
    }

    resultNotification = null;

    try {
      const valid = await schema
        .validate(metadataToUpdate)
        .then(() => {
          errors = {};
          return true;
        })
        .catch((error) => {
          errors = { ...errors, [error.path]: error.message };
          return false;
        });

      if (!valid) {
        resultNotification = {
          kind: 'error',
          title: 'Validation Error:',
          subtitle: 'Please fix the errors in the form and try again.',
        };
        return;
      }

      loading = true;

      await onSaveMetadata(entitlementId, { ...metadataToUpdate, serviceId });
      dispatch('close');
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.details?.[0]?.context?.message ??
        getServerErrorMessage(error) ??
        'Verify your submission and try again!';
      console.error('[PermissionDelayModal] Failed to update entitlement metadata!', errorMessage);
      resultNotification = {
        kind: 'error',
        title: 'Error:',
        subtitle: `Failed to update entitlement metadata! ${errorMessage}`,
      };
    } finally {
      loading = false;
    }
  }

  async function deleteEntitlementMetadata() {
    resultNotification = null;
    loading = true;

    try {
      await onDeleteMetadata(entitlementId);
      dispatch('close');
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.details?.[0]?.context?.message ??
        getServerErrorMessage(error) ??
        'Verify your submission and try again!';
      console.error('[PermissionDelayModal] Failed to delete entitlement metadata!', errorMessage);
      resultNotification = {
        kind: 'error',
        title: 'Error:',
        subtitle: `Failed to delete entitlement metadata! ${errorMessage}`,
      };
    } finally {
      loading = false;
    }
  }

  function handleCustomChangeEvent(key) {
    return function onCustomChange({ detail: value }) {
      metadataToUpdate = {
        ...metadataToUpdate,
        ...(key === 'delayMonths' && !value ? { calendarMonthDelay: false } : {}),
        [key]: value,
      };
    };
  }

  function handleClose() {
    if (loading) {
      return;
    }

    dispatch('close');
  }

  function isSameAsAnyOfSaved() {
    const entitlementMetadataMatch =
      entitlementMetadata &&
      !isEmpty(entitlementMetadata) &&
      isEqual(metadataToUpdate, {
        delayMinutes: entitlementMetadata.delayMinutes || 0,
        delayHours: entitlementMetadata.delayHours || 0,
        delayDays: entitlementMetadata.delayDays || 0,
        delayMonths: entitlementMetadata.delayMonths || 0,
        calendarMonthDelay: entitlementMetadata.calendarMonthDelay || false,
      });

    return (
      isEqual(metadataToUpdate, {
        delayMinutes: feedMetadata?.defaultPermissionDelayMinutes || 0,
        delayHours: feedMetadata?.defaultPermissionDelayHours || 0,
        delayDays: feedMetadata?.defaultPermissionDelayDays || 0,
        delayMonths: feedMetadata?.defaultPermissionDelayMonths || 0,
        calendarMonthDelay: feedMetadata?.defaultPermissionCalendarMonthDelay || false,
      }) || entitlementMetadataMatch
    );
  }

  function findNowInFeedTimezone(feedTimezone) {
    try {
      return feedTimezone ? dayjs().tz(feedTimezone).format('YYYY/MM/DD HH:mm') : 'N/A';
    } catch (error) {
      console.error(`Failed to find current time in feed timezone (${feedTimezone}).`, error);
      return 'N/A';
    }
  }

  $: noSavedEntitlementMetadata = !entitlementMetadata || isEmpty(entitlementMetadata);
  $: toUpdateDelayText =
    `${formatDurationUnitsToText(
      metadataToUpdate.delayMonths,
      metadataToUpdate.delayDays,
      metadataToUpdate.delayHours,
      metadataToUpdate.delayMinutes
    )}${metadataToUpdate.calendarMonthDelay ? ' (Calendar Month Delay)' : ''}` || 'No Delay';
  $: isSameAsDefaultOrSaved = isSameAsAnyOfSaved(metadataToUpdate);
  $: nowInFeedTimezone = findNowInFeedTimezone(feedMetadata?.timezone);
</script>

<Modal
  modalHeading="Overwrite Delay"
  modalLabel={feedName}
  primaryButtonText="Update"
  secondaryButtons={!noSavedEntitlementMetadata ? [{ text: 'Cancel' }, { text: 'Use Default' }] : [{ text: 'Cancel' }]}
  size="md"
  shouldSubmitOnEnter={false}
  preventCloseOnClickOutside
  primaryButtonDisabled={loading || isSameAsDefaultOrSaved}
  bind:open
  on:click:button--secondary={({ detail }) => {
    if (detail.text === 'Cancel') handleClose();
    if (detail.text === 'Use Default') deleteEntitlementMetadata();
  }}
  on:close={handleClose}
  on:submit={updateEntitlementMetadata}
>
  <LoadingSpinner small {loading}>
    {#if resultNotification}
      <InlineNotification
        kind={resultNotification.kind}
        lowContrast
        title={resultNotification.title}
        subtitle={resultNotification.subtitle}
      />
    {/if}
    <p>You can overwrite the default delay using the fields below.</p>
    <p><strong>Caution! This change will only be applied to this specific entitlement.</strong></p>
    <br />
    <p>
      <strong>Trading End Time:</strong>
      {feedMetadata?.tradingStartTime} | <strong>Now:</strong>
      {nowInFeedTimezone} <em>({feedMetadata?.timezone})</em>
    </p>
    <p>Current delay: <strong>{activeDelayText}</strong> <Tag size="sm">{isDefaultDelay ? 'default' : 'overwritten'}</Tag></p>
    {#if !isDefaultDelay}
      <p>Default delay: <strong>{defaultDelayText}</strong></p>
    {/if}
    <br />
    <p><em>Delay to apply: <strong>{toUpdateDelayText}</strong></em></p>
    <br />
    <div class="form">
      <div class="time-select-wrapper number-inputs">
        <div class="month-delay-wrapper">
          <NumberInput
            labelText="Months"
            name="delayMonths"
            size="sm"
            min={0}
            placeholder="0"
            value={metadataToUpdate.delayMonths}
            invalid={!!errors?.delayMonths}
            invalidText={errors?.delayMonths ?? 'Invalid value'}
            on:change={handleCustomChangeEvent('delayMonths')}
            disableWheel
          />
          <Checkbox
            name="calendarMonthDelay"
            labelText="Calendar Month Delay"
            disabled={!metadataToUpdate.delayMonths}
            checked={metadataToUpdate.calendarMonthDelay}
            on:check={handleCustomChangeEvent('calendarMonthDelay')}
          />
        </div>
        <NumberInput
          labelText="Days"
          name="delayDays"
          size="sm"
          min={0}
          placeholder="0"
          value={metadataToUpdate.delayDays}
          invalid={!!errors?.delayDays}
          invalidText={errors?.delayDays ?? 'Invalid value'}
          on:change={handleCustomChangeEvent('delayDays')}
          disableWheel
        />
        <NumberInput
          labelText="Hours"
          name="delayHours"
          size="sm"
          min={0}
          max={23}
          placeholder="0"
          value={metadataToUpdate.delayHours}
          invalid={!!errors?.delayHours}
          invalidText={errors?.delayHours ?? 'Invalid value'}
          on:change={handleCustomChangeEvent('delayHours')}
          disableWheel
        />
        <NumberInput
          labelText="Minutes"
          name="delayMinutes"
          size="sm"
          min={0}
          max={59}
          placeholder="0"
          value={metadataToUpdate.delayMinutes}
          invalid={!!errors?.delayMinutes}
          invalidText={errors?.delayMinutes ?? 'Invalid value'}
          on:change={handleCustomChangeEvent('delayMinutes')}
          disableWheel
        />
      </div>
    </div>
  </LoadingSpinner>
</Modal>

<style>
  .form :global(.bx--form-item .bx--text-area__label-wrapper) {
    margin-top: 1rem;
  }

  .time-select-wrapper.number-inputs :global(.bx--label) {
    margin-bottom: 0.25rem;
    margin-top: 0.5rem;
  }

  .month-delay-wrapper {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
  }

  .month-delay-wrapper :global(.bx--checkbox-label) {
    margin-left: 0.5rem;
  }

  .month-delay-wrapper :global(.bx--checkbox-label-text) {
    display: flex;
    align-items: center;
    font-size: 0.75rem;
  }
</style>
